import { type PopoverPlacement, placePopover } from '../../../components/ui/popoverPlacement.js';

/**
 * 404 (#1329): the schedule's pop-ups close on a window resize or scroll so they never float away from their bar on a
 * laptop. On a phone, the on-screen keyboard resizes and scrolls the window when a field is tapped — and again when it
 * closes — so a pop-up with a field (and the slot it was confirming) closed while staff typed. On a touch screen such a
 * pop-up is never closed by a resize or scroll; it is re-placed inside the visible area (above the keyboard) instead,
 * and the first tap outside while typing only ends the typing. A mouse (fine pointer) keeps exactly today's behaviour.
 */
const FIELD = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

const coarse = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

/** A field inside the pop-up has focus, on a touch screen. */
export function typingInside(box: HTMLElement | null): boolean {
  if (!box || !coarse()) return false;
  const active = document.activeElement;
  return active instanceof HTMLElement && box.contains(active) && active.matches(FIELD);
}

/** The pop-up holds a field, on a touch screen: resizes and scrolls (the keyboard) must not close it. */
export function touchForm(box: HTMLElement | null): boolean {
  return !!box && coarse() && box.querySelector(FIELD) !== null;
}

/**
 * Where the pop-up goes. A mouse: inside the window, exactly as before. A touch screen: inside the VISUAL viewport —
 * the part of the page above the on-screen keyboard — so the pop-up's buttons stay reachable while staff type.
 */
export function placeInView(
  anchor: { top: number; bottom: number; left: number },
  size: { width: number; height: number },
): PopoverPlacement {
  const vv = coarse() ? window.visualViewport : null;
  if (!vv)
    return placePopover(anchor, size, { width: window.innerWidth, height: window.innerHeight });
  const p = placePopover(
    {
      top: anchor.top - vv.offsetTop,
      bottom: anchor.bottom - vv.offsetTop,
      left: anchor.left - vv.offsetLeft,
    },
    size,
    { width: vv.width, height: vv.height },
  );
  return { ...p, top: p.top + vv.offsetTop, left: p.left + vv.offsetLeft };
}

/** Re-place on every change of the visible area (the keyboard opening or closing). Returns the clean-up. */
export function onVisibleAreaChange(place: () => void): () => void {
  const vv = window.visualViewport;
  window.addEventListener('resize', place);
  vv?.addEventListener('resize', place);
  vv?.addEventListener('scroll', place);
  return () => {
    window.removeEventListener('resize', place);
    vv?.removeEventListener('resize', place);
    vv?.removeEventListener('scroll', place);
  };
}
